/**
 * Extracție date din cuburile Zarr la coordonatele orașelor.
 * Refolosește nearestIndex/extractTimeSeries din data.js.
 */
import { nearestIndex, extractTimeSeries } from './data.js';
import { getAqiCategory, LABELS, UNITS } from './colormaps.js';
import { CITIES } from './cities.js';

/**
 * Cache indecși lat/lon per oraș per cub (evită recalcularea).
 * Cheia e `${cubeId}_${cityId}` (cubeId e join-ul shape-ului).
 */
const indexCache = new Map();

function getCubeId(cube) {
  return cube.shape.join('x');
}

function getCityIndices(cube, city) {
  const key = `${getCubeId(cube)}_${city.id}`;
  if (indexCache.has(key)) return indexCache.get(key);

  const latIdx = nearestIndex(cube.lats, city.lat);
  const lonIdx = nearestIndex(cube.lons, city.lon);
  const result = { latIdx, lonIdx };
  indexCache.set(key, result);
  return result;
}

/**
 * Extrage valoarea la un anume timestep pentru un oraș.
 */
export function sampleCityValue(cube, city, timeIndex) {
  const { latIdx, lonIdx } = getCityIndices(cube, city);
  const [, ny, nx] = cube.shape;
  const v = cube.data[timeIndex * ny * nx + latIdx * nx + lonIdx];
  return Number.isNaN(v) ? null : v;
}

/**
 * Extrage valorile pentru toate orașele la un timestep.
 * Returnează array de { city, value, category } sortate descrescător.
 */
export function sampleAllCities(cube, variable, timeIndex) {
  const results = CITIES.map(city => {
    const value = sampleCityValue(cube, city, timeIndex);
    const category = getAqiCategory(value, variable);
    return { city, value, category };
  });

  // Sortare descrescătoare (cele mai poluate primele, dar orașul selectat va fi mutat separat)
  results.sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));
  return results;
}

/**
 * Extrage o serie temporală scurtă pentru sparkline-ul unui oraș.
 * @param {object} cube
 * @param {object} city
 * @param {number} centerIdx - Indexul de timp curent (centrul)
 * @param {number} [numSteps=8] - Câți pași (8 pași × 3h = 24h meteo, 8h AQ)
 * @returns {number[]} Serie de valori
 */
export function getCitySparkline(cube, city, centerIdx, numSteps = 8) {
  const { latIdx, lonIdx } = getCityIndices(cube, city);
  const series = extractTimeSeries(cube, latIdx, lonIdx);

  // Window: de la centerIdx înapoi, până la numSteps înainte
  const start = Math.max(0, centerIdx - Math.floor(numSteps / 2));
  const end = Math.min(series.length, start + numSteps);
  return series.slice(start, end);
}

/**
 * Generează o descriere textuală a situației.
 */
export function generateDescription(value, variable, cityName) {
  if (value == null || isNaN(value)) {
    return `Date indisponibile pentru ${cityName}.`;
  }

  const cat = getAqiCategory(value, variable);
  const label = LABELS[variable] || variable;
  const unit = UNITS[variable] || '';
  const valStr = value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(1);

  // Descrieri diferite per tip variabilă
  const isAQ = ['pm25', 'pm10', 'no2', 'o3', 'so2', 'co'].includes(variable);

  if (isAQ) {
    const catLabel = cat?.label?.toLowerCase() || '—';
    const descriptions = {
      'bun': `Calitatea aerului în ${cityName} este bună — ${label} la ${valStr} ${unit}.`,
      'acceptabil': `Calitate acceptabilă în ${cityName} — ${label} la ${valStr} ${unit}. Persoanele sensibile pot avea disconfort ușor.`,
      'moderat': `Atenție moderată în ${cityName} — ${label} la ${valStr} ${unit}. Grupurile sensibile ar trebui să limiteze activitățile în aer liber.`,
      'slab': `Calitate slabă în ${cityName} — ${label} la ${valStr} ${unit}. Recomandare de a evita activitățile prelungite în exterior.`,
      'foarte slab': `Aer foarte poluat în ${cityName} — ${label} la ${valStr} ${unit}! Evitați expunerea prelungită.`,
      'extrem': `⚠️ Poluare extremă în ${cityName} — ${label} la ${valStr} ${unit}! Rămâneți în interior.`,
    };
    return descriptions[catLabel] || `${label} în ${cityName}: ${valStr} ${unit} — ${catLabel}.`;
  }

  // Meteo
  if (variable === 'temp2m') {
    if (value < 0) return `Temperaturi sub zero în ${cityName}: ${valStr}°C. Ger și posibil polei.`;
    if (value < 10) return `Răcoare în ${cityName}: ${valStr}°C. Îmbrăcați-vă gros.`;
    if (value < 20) return `Temperatură plăcută în ${cityName}: ${valStr}°C.`;
    if (value < 30) return `Cald în ${cityName}: ${valStr}°C. Hidratare recomandată.`;
    return `Caniculă în ${cityName}: ${valStr}°C! Evitați expunerea la soare.`;
  }

  if (variable === 'wind_speed') {
    if (value < 2) return `Liniște atmosferică în ${cityName}: vânt de ${valStr} m/s.`;
    if (value < 5) return `Briză ușoară în ${cityName}: ${valStr} m/s.`;
    if (value < 10) return `Vânt moderat în ${cityName}: ${valStr} m/s.`;
    return `Vânt puternic în ${cityName}: ${valStr} m/s! Atenție la rafale.`;
  }

  if (variable === 'cloud') {
    if (value < 25) return `Cer senin în ${cityName}: nebulozitate ${valStr}%.`;
    if (value < 50) return `Parțial înnorat în ${cityName}: ${valStr}% acoperire.`;
    if (value < 75) return `Variabil în ${cityName}: ${valStr}% acoperire noroasă.`;
    return `Cer acoperit în ${cityName}: ${valStr}% nebulozitate.`;
  }

  if (variable === 'precip') {
    if (value < 0.5) return `Fără precipitații semnificative în ${cityName}.`;
    if (value < 2) return `Precipitații ușoare în ${cityName}: ${valStr} mm.`;
    if (value < 10) return `Ploaie moderată în ${cityName}: ${valStr} mm.`;
    return `Precipitații intense în ${cityName}: ${valStr} mm!`;
  }

  return `${label} în ${cityName}: ${valStr} ${unit}.`;
}
